import type {
  AiStepRunStatus,
  AiStepRunTrigger,
  AiSummaryKind,
  AiSummaryStep,
  ExpertAiStepRun,
  ExpertAiSummaryOverview,
} from "@shopexperts/legacy-api";
import { extractServerMessage, extractStatus, mapValidationErrors } from "@/app/composables/claim/claimApiErrors";

export const AI_SUMMARY_STEP_ORDER: readonly AiSummaryStep[] = [
  "review_themes",
  "fact_extraction",
  "normalize_evidence",
  "profile_summary",
  "reviews_summary",
];

export const AI_SUMMARY_STEP_LABELS: Record<AiSummaryStep, string> = {
  review_themes: "Review themes",
  fact_extraction: "Fact extraction",
  normalize_evidence: "Normalize evidence",
  profile_summary: "Expert summary",
  reviews_summary: "Reviews summary",
};

export const AI_RUN_TRIGGER_LABELS: Record<AiStepRunTrigger, string> = {
  manual: "Manual",
  console: "Console",
  cascade: "Cascade",
  partner_sync: "Partner sync",
};

export const AI_SUMMARY_STEP_DESCRIPTIONS: Record<AiSummaryStep, string> = {
  review_themes: "Reads every published review and extracts recurring themes with quoted evidence.",
  fact_extraction: "Pulls the facts that matter out of the public profile, services, FAQs, and stories.",
  normalize_evidence: "Merges themes, facts, verified signals, and admin context into one evidence set.",
  profile_summary: "Writes the expert summary shown on the profile from the normalized evidence.",
  reviews_summary: "Writes the reviews summary shown in the reviews block from the normalized evidence.",
};

export const AI_SUMMARY_KIND_LABELS: Record<AiSummaryKind, string> = {
  profile: "Expert summary",
  reviews: "Reviews summary",
};

export const AI_SUMMARY_KIND_STEP: Record<AiSummaryKind, AiSummaryStep> = {
  profile: "profile_summary",
  reviews: "reviews_summary",
};

export const AI_STEP_RUN_STATUS_LABELS: Record<AiStepRunStatus, string> = {
  pending: "Queued",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  skipped: "Skipped",
};

export type AiStatusChipVariant = "success" | "warn" | "neutral" | "dark" | "outline";

export function aiStatusChipVariant(status: AiStepRunStatus): AiStatusChipVariant {
  switch (status) {
    case "succeeded":
      return "success";
    case "failed":
      return "warn";
    case "running":
      return "dark";
    case "pending":
      return "outline";
    case "skipped":
    default:
      return "neutral";
  }
}

export function isAiRunActive(status: AiStepRunStatus | null | undefined): boolean {
  return status === "pending" || status === "running";
}

export function overviewHasActiveRuns(overview: ExpertAiSummaryOverview | null | undefined): boolean {
  if (!overview) return false;
  return AI_SUMMARY_STEP_ORDER.some((step) => isAiRunActive(overview.steps[step].latest_run?.status));
}

export function overviewHasAnyRun(overview: ExpertAiSummaryOverview | null | undefined): boolean {
  if (!overview) return false;
  return AI_SUMMARY_STEP_ORDER.some((step) => overview.steps[step].latest_run !== null);
}

export function formatAiDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export function formatAiDuration(run: Pick<ExpertAiStepRun, "started_at" | "finished_at">): string | null {
  if (!run.started_at || !run.finished_at) return null;
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

/** The API stores cost as decimal(10,6); show it untouched so fractions of a cent stay visible. */
export function formatAiCost(cost: number | null | undefined): string | null {
  if (typeof cost !== "number" || !Number.isFinite(cost)) return null;
  if (cost === 0) return "$0";
  return `$${cost.toFixed(6).replace(/0+$/, "").replace(/\.$/, "")}`;
}

export function formatAiTokens(run: Pick<ExpertAiStepRun, "usage">): string | null {
  const { prompt_tokens: prompt, completion_tokens: completion } = run.usage;
  if (prompt == null && completion == null) return null;
  const format = (value: number | null) => (value == null ? "—" : value.toLocaleString("en-US"));
  return `${format(prompt)} in / ${format(completion)} out`;
}

export function formatAiModel(model: string | null | undefined): string {
  if (!model) return "—";
  const slash = model.lastIndexOf("/");
  return slash === -1 ? model : model.slice(slash + 1);
}

export function shortPromptVersion(version: string | null | undefined): string | null {
  return version ? version.slice(0, 7) : null;
}

export function aiSummariesErrorMessage(error: unknown): string {
  const fieldError = Object.values(mapValidationErrors(error))[0];
  if (fieldError) return fieldError;
  const serverMessage = extractServerMessage(error);
  if (serverMessage && serverMessage !== "Validation failed.") return serverMessage;
  return "Something went wrong. Please try again.";
}

export type AiSummariesBlockedReason = "disabled" | "not_configured" | null;

interface ApiErrorLike {
  response?: { status?: number; data?: { code?: unknown } };
}

export function aiSummariesBlockedReason(error: unknown): AiSummariesBlockedReason {
  if (extractStatus(error) !== 409) return null;
  const code = (error as ApiErrorLike)?.response?.data?.code;
  if (code === "ai_summaries_disabled") return "disabled";
  if (code === "ai_provider_not_configured") return "not_configured";
  return null;
}

export function stringifyAiJson(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
